import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { ConfigContext } from "../../Context/ConfigContext";
import { Helmet } from "react-helmet";
import Swal from "sweetalert2";
import { OverlayLoader } from "../../Components/Shimmers";
import { axiosGet, axiosPost } from "../../Apis/axiosMethods";
import Select from "react-select";
import { GetPointsRangeData, GetSortByStatus, GetCashRangeData } from "../../Utils/Cities";
import PointsCardsSummary from "./RedeemOffer/PointsCardsSummary";

const RedeemPoints = () => {

    const { t } = useTranslation();
    const { business_id, business_user_id } = useContext(ConfigContext);

    const [offers, setOffers] = useState([]);
    const [rewardsPointsStates, setRewardsPointsStates] = useState({});
    const [offerCategories, setOfferCategories] = useState([]);
    const [selectedOfferCategories, setSelectedOfferCategories] = useState(null);
    const [showRedeemableOnly, setShowRedeemableOnly] = useState(false);
    const [loading, setLoading] = useState(false);

    const [offerPoints, setOfferPoints] = useState([]);
    const [offerCashData, setOfferCashData] = useState([]);
    const [offerBrands, setOfferBrands] = useState([]);
    const [offerOccasionData, setOfferOccasionData] = useState([]);

    const [selectedPoints, setSelectedPoints] = useState(null);
    const [selectedCashValue, setSelectedCashValue] = useState(null);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [selectedOccasion, setSelectedOccasion] = useState(null);
    const [selectedSortBy, setSelectedSortBy] = useState(null);

    const GetOffers = async () => {
        try {
            setLoading(true);

            const params = {};

            if (selectedOfferCategories !== null) params.keyword = selectedOfferCategories;

            if (selectedPoints) {
                params.min_points = selectedPoints.min_points;
                params.max_points = selectedPoints.max_points;
            }

            if (selectedCashValue) {
                params.min_cash_value = selectedCashValue.min_cash_value;
                params.max_cash_value = selectedCashValue.max_cash_value;
            }

            if (selectedBrand?.value) params.business_redeem_offer_brand_name = selectedBrand.value;

            if (selectedOccasion?.value) params.business_redeem_offer_tag = selectedOccasion.value;

            if (selectedSortBy?.value) params.sort_by = selectedSortBy.value;

            const response = await axiosGet("/Users/GetOfferList", params);

            if (response?.data.success) {
                setOffers(Array.isArray(response?.data?.data) ? response?.data?.data : []);
            } else {
                setOffers([]);
            }
        } catch (error) {
            console.error("Get Offers Error:", error);
            setOffers([]);
        } finally {
            setLoading(false);
        }
    };

    const GetRewardsPointsStates = async () => {
        try {
            const response = await axiosGet("/Users/GetRewardsPointsStates", { business_id, business_user_id });

            if (response?.data?.success) {
                setRewardsPointsStates(response?.data?.data && typeof response?.data?.data === "object" ? response?.data?.data : {});
            } else {
                setRewardsPointsStates({});

                Swal.fire({
                    title: "Error",
                    text: response?.data?.message || "Unable to fetch reward points.",
                    icon: "error",
                    confirmButtonText: "Okay"
                });
            }
        } catch (error) {
            console.error("Get Rewards Points States Error:", error);
            setRewardsPointsStates({});

            Swal.fire({
                title: "Error",
                text: "An error occurred while fetching the reward points.",
                icon: "error",
                confirmButtonText: "Okay"
            });
        }
    };

    const GetOfferCategories = async () => {
        try {
            const response = await axiosGet("/Users/GetParentOfferCategories");

            if (response?.data?.success) {
                setOfferCategories(Array.isArray(response?.data?.data) ? response.data?.data : []);
            } else {
                setOfferCategories([]);
            }
        } catch (error) {
            console.error("Get Offer Categories Error:", error);
            setOfferCategories([]);
        }
    };

    const SendRequest = async (business_redeem_offer_id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "Do you want to redeem this reward?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Redeem",
            cancelButtonText: "Cancel",
            reverseButtons: true
        });

        if (!result.isConfirmed) return;

        try {

            const data = await axiosPost(`/Users/RedeemRequest`, {
                business_id, business_user_id, business_redeem_offer_id
            });

            if (data?.success) {
                await GetRewardsPointsStates();

                Swal.fire({
                    title: "Redemption Successful!",
                    html: `<p>You have successfully redeemed <b>${data?.points || 0} points</b>.</p><p>🎉 Your reward will be processed and delivered shortly.</p>`,
                    icon: "success",
                    confirmButtonText: "Okay"
                });
            } else {
                Swal.fire({
                    title: "False",
                    text: data?.message || "You don't have enough points.",
                    icon: "error",
                    confirmButtonText: "Okay"
                });
            }
        } catch (error) {
            console.error("Redeem Request Error:", error);

            Swal.fire({
                title: "Error",
                text: error?.response?.data?.message || "An error occurred while redeeming.",
                icon: "error",
                confirmButtonText: "Okay"
            });
        }
    };

    const batchColor = (category) => {
        switch (category) {
            case "Gift Cards":
                return "bg-gift-cards";
            case "Travel and Experiences":
                return "bg-travel";
            case "Home Essentials":
                return "bg-home-essentials";
            case "Fashion Accessories":
                return "bg-fashion";
            case "Gaming":
                return "bg-gaming";
            case "Tech and Electronics":
                return "bg-tech";
            default:
                return "bg-secondary";
        }
    };

    const GetPointsData = async () => {
        try {
            const response = await axiosGet("/Users/GetRedeemOfferPoints");

            if (response?.data.success) {
                const minPoints = Number(response?.data?.min_points || 0);
                const maxPoints = Number(response?.data?.max_points || 0);
                setOfferPoints(GetPointsRangeData(minPoints, maxPoints));
            } else {
                setOfferPoints([]);
            }
        } catch (error) {
            console.error("Get Points Data Error:", error);
            setOfferPoints([]);
        }
    };

    const GetCashData = async () => {
        try {
            const response = await axiosGet("/Users/GetRedeemOfferCashValues");

            if (response?.data.success) {
                const minCash = Number(response?.data?.min_cash_value || 0);
                const maxCash = Number(response?.data?.max_cash_value || 0);

                const cashRanges = GetCashRangeData(minCash, maxCash).map((item) => ({
                    value: `${item.min_points}-${item.max_points}`,
                    label: `${item.min_points} - ${item.max_points}`,
                    min_cash_value: item.min_points,
                    max_cash_value: item.max_points
                }));

                setOfferCashData(cashRanges);
            } else {
                setOfferCashData([]);
            }
        } catch (error) {
            console.error("Get Cash Data Error:", error);
            setOfferCashData([]);
        }
    };

    const GetPointsBrandsData = async () => {
        try {
            const response = await axiosGet("/Users/GetRedeemOfferBrands");

            if (response?.data.success) {
                setOfferBrands(
                    Array.isArray(response?.data?.data)
                        ? response.data.data
                            .filter((item) => item?.business_redeem_offer_brand_name)
                            .map((item) => ({
                                value: item.business_redeem_offer_brand_name,
                                label: item.business_redeem_offer_brand_name
                            }))
                        : []
                );
            } else {
                setOfferBrands([]);
            }
        } catch (error) {
            console.error("Get Brands Data Error:", error);
            setOfferBrands([]);
        }
    };

    const GetOccasionData = async () => {
        try {
            const response = await axiosGet("/Users/GetRedeemOfferOccasion");

            if (response?.data.success) {
                setOfferOccasionData(
                    Array.isArray(response?.data.data)
                        ? response.data.data
                            .filter((item) => item?.business_redeem_offer_tag)
                            .map((item) => ({
                                value: item.business_redeem_offer_tag,
                                label: item.business_redeem_offer_tag
                            }))
                        : []
                );
            } else {
                setOfferOccasionData([]);
            }
        } catch (error) {
            console.error("Get Occasion Data Error:", error);
            setOfferOccasionData([]);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        GetRewardsPointsStates();
        GetOfferCategories();
        GetPointsData();
        GetPointsBrandsData();
        GetOccasionData();
        GetCashData();
    }, []);

    useEffect(() => {
        GetOffers();
    }, [selectedOfferCategories, selectedPoints, selectedCashValue, selectedBrand, selectedOccasion, selectedSortBy]);

    const availablePoints = Number(rewardsPointsStates?.availabe_points || 0);

    const displayedOffers = showRedeemableOnly
        ? offers.filter((item) => Number(item?.business_redeem_offer_points || 0) <= availablePoints)
        : offers;

    const selectStyles = {
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        menu: (base) => ({ ...base, zIndex: 9999 })
    };

    const ResetFilters = () => {
        setSelectedOfferCategories(null);
        setSelectedPoints(null);
        setSelectedCashValue(null);
        setSelectedBrand(null);
        setSelectedOccasion(null);
        setSelectedSortBy(null);
        setShowRedeemableOnly(false);
    };

    return (
        <>
            {loading ? (
                <div style={{ height: "70vh" }}>
                    <OverlayLoader />
                </div>
            ) : (
                <main style={{ marginTop: "-10px" }}>
                    <Helmet>
                        <title>Spare Parts Redeem Points - {t("site_name")}</title>
                        <meta name="description" content={`Find the best spare parts suppliers at ${t("site_name")}. We offer a wide range of high-quality parts to meet your needs.`} />
                    </Helmet>

                    <div className="container">
                        <nav aria-label="breadcrumb" className="breadcrumb-nav my-3">
                            <ol className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <NavLink to="/">
                                        <i className="icon-home" />
                                    </NavLink>
                                </li>
                                <li className="breadcrumb-item active">Redeem Points</li>
                            </ol>
                        </nav>
                    </div>

                    <div className="container">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h3 className="mb-0">Redeem Your Points</h3>

                            <NavLink to="/MyAccount/RequestedRedeemPoints" className="btn btn-outline-primary btn-sm">
                                <i className="fas fa-gift mr-2"></i>
                                Requested Redeem Points
                            </NavLink>
                        </div>

                        <PointsCardsSummary
                            rewardsPointsStates={rewardsPointsStates}
                            offers={offers}
                        />


                        <div className="row mb-2">
                            <div className="col-12">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="redeem-category-navbar flex-grow-1">
                                        <div
                                            className={`redeem-category-item text-uppercase mr-2 ${selectedOfferCategories === null ? "active" : ""
                                                }`}
                                            onClick={() => setSelectedOfferCategories(null)}
                                        >
                                            All
                                        </div>

                                        {offerCategories.map((category) => {
                                            const hasChildren =
                                                Array.isArray(category?.children) &&
                                                category.children.length > 0;

                                            const isSelected =
                                                selectedOfferCategories ===
                                                category?.business_redeem_offers_category_id;

                                            return (
                                                <div
                                                    className="redeem-category-wrapper"
                                                    key={category?.business_redeem_offers_category_id}
                                                >
                                                    <div
                                                        className={`redeem-category-item mr-2 ${isSelected ? "active" : ""
                                                            }`}
                                                        onClick={() =>
                                                            !hasChildren &&
                                                            setSelectedOfferCategories(
                                                                category?.business_redeem_offers_category_id
                                                            )
                                                        }
                                                    >
                                                        <span>
                                                            {category?.business_redeem_offers_category_name}
                                                        </span>

                                                        {hasChildren && (
                                                            <i className="fas fa-chevron-down ml-2"></i>
                                                        )}
                                                    </div>

                                                    {hasChildren && (
                                                        <div className="redeem-category-dropdown">
                                                            <div
                                                                className={`redeem-category-child ${selectedOfferCategories ===
                                                                    category?.business_redeem_offers_category_id
                                                                    ? "active"
                                                                    : ""
                                                                    }`}
                                                                onClick={() =>
                                                                    setSelectedOfferCategories(
                                                                        category?.business_redeem_offers_category_id
                                                                    )
                                                                }
                                                            >
                                                                View All
                                                            </div>

                                                            {category.children.map((child) => (
                                                                <div
                                                                    key={
                                                                        child?.business_redeem_offers_category_id
                                                                    }
                                                                    className={`redeem-category-child ${selectedOfferCategories ===
                                                                        child?.business_redeem_offers_category_id
                                                                        ? "active"
                                                                        : ""
                                                                        }`}
                                                                    onClick={() =>
                                                                        setSelectedOfferCategories(
                                                                            child?.business_redeem_offers_category_id
                                                                        )
                                                                    }
                                                                >
                                                                    {
                                                                        child?.business_redeem_offers_category_name
                                                                    }
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm px-3 ml-3"
                                        onClick={ResetFilters}
                                    >
                                        <i className="fas fa-redo mr-2"></i>
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>


                        <div className="row mb-1">
                            <div className="col-12">
                                <div className="d-flex align-items-center justify-content-between border points-summary">
                                    <div className="d-flex align-items-center ml-4">
                                        <p className="mb-0">
                                            You have <span style={{ color: "#000", fontWeight: "bold", fontSize: "18px" }}>{availablePoints}</span> Points
                                        </p>

                                        <div className="ml-5">
                                            <span className="badge badge-success-subtle">Available to redeem now</span>
                                        </div>

                                    </div>

                                    <div className="d-flex align-items-center">
                                        <span className="mr-3">Show what i can redeem</span>

                                        <div className="redeem-switch">
                                            <input type="checkbox" id="showRedeemableOnly" checked={showRedeemableOnly} onChange={(e) => setShowRedeemableOnly(e.target.checked)} />

                                            <label htmlFor="showRedeemableOnly">
                                                <span className="switch-off">off</span>
                                                <span className="switch-on">on</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-1">
                            <div className="col-lg col-md-6">
                                <label>
                                    <span style={{ fontSize: '16px', fontWeight: "bold", textAlign: "center" }}>
                                        Smart Filters
                                    </span>
                                </label>
                                <Select
                                    options={offerPoints}
                                    value={selectedPoints}
                                    onChange={setSelectedPoints}
                                    styles={selectStyles}
                                    placeholder="Points"
                                    menuPortalTarget={document.body}
                                    isClearable
                                />
                            </div>

                            <div className="col-lg col-md-6">
                                <label className="mb-1">Cash Value</label>
                                <Select
                                    options={offerCashData}
                                    value={selectedCashValue}
                                    onChange={setSelectedCashValue}
                                    styles={selectStyles}
                                    placeholder="Select Cash Values..."
                                    menuPortalTarget={document.body}
                                    isClearable
                                />
                            </div>

                            <div className="col-lg col-md-6">
                                <label className="mb-1">Brands</label>
                                <Select
                                    options={offerBrands}
                                    value={selectedBrand}
                                    onChange={setSelectedBrand}
                                    styles={selectStyles}
                                    placeholder="Brands"
                                    menuPortalTarget={document.body}
                                    isClearable
                                />
                            </div>

                            <div className="col-lg col-md-6">
                                <label className="mb-1">Occasion</label>
                                <Select
                                    options={offerOccasionData}
                                    value={selectedOccasion}
                                    onChange={setSelectedOccasion}
                                    styles={selectStyles}
                                    placeholder="Occasion"
                                    menuPortalTarget={document.body}
                                    isClearable
                                />
                            </div>

                            <div className="col-lg col-md-6">
                                <label className="mb-1">Sort By</label>
                                <Select
                                    options={GetSortByStatus}
                                    value={selectedSortBy}
                                    onChange={setSelectedSortBy}
                                    styles={selectStyles}
                                    placeholder="Select Sort By..."
                                    menuPortalTarget={document.body}
                                    isClearable
                                />
                            </div>
                        </div>

                        <div className="row mb-1">
                            <div className="col-12">
                                <span style={{ fontSize: "20px", fontWeight: "bold" }}>You can redeem these now</span>
                            </div>
                        </div>

                        <div className="row">
                            {displayedOffers.length > 0 ? (
                                displayedOffers.map((item, index) => (
                                    <div key={item?.business_redeem_offer_id || index} className="col-md-2-4 mb-4">
                                        <div className="offer-card h-100 d-flex flex-column justify-content-between shadow-sm rounded border position-relative">
                                            <div className={`position-absolute top-0 end-0 px-3 py-1 text-white fw-semibold ${batchColor(item?.business_redeem_offers_category_name)}`} style={{ borderBottomLeftRadius: "20px", fontSize: "13px", zIndex: 2 }}>
                                                {item?.business_redeem_offers_category_name || "-"}
                                            </div>

                                            <NavLink to={`/MyAccount/RedeemPointsDetails/${item?.business_redeem_offer_id}`}>
                                                <div className="offer-image">
                                                    <img src={item?.business_redeem_offer_image_url} alt={item?.business_redeem_offer_title || "Reward"} />
                                                </div>
                                            </NavLink>

                                            <div className="product-details p-3 d-flex flex-column flex-grow-1">
                                                <div>
                                                    <h6 className="text-truncate mb-1">
                                                        <NavLink to={`/MyAccount/RedeemPointsDetails/${item?.business_redeem_offer_id}`}>
                                                            <span className="text-dark">{item?.business_redeem_offer_title || "Product Name"}</span>
                                                        </NavLink>
                                                    </h6>

                                                    <p className="text-muted small offer-desc mb-2">{item?.business_redeem_offer_description || ""}</p>
                                                </div>

                                                <div className="d-flex justify-content-between align-items-center mt-auto">
                                                    <span className="font-weight-bold text-dark">
                                                        {Number(item?.business_redeem_offer_points || 0)}
                                                        <small className="ml-1">Pts</small>
                                                    </span>

                                                    <button type="button" className="btn btn-primary btn-sm redeem-btn" onClick={() => SendRequest(item?.business_redeem_offer_id)}>
                                                        Buy it now
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-12 text-center py-5">
                                    <h5 className="text-muted">
                                        {showRedeemableOnly ? "No rewards available with your current points." : "No offers available at the moment."}
                                    </h5>
                                </div>
                            )}
                        </div>
                    </div>

                    <style>
                        {`
                            .col-md-2-4 { position: relative; width: 100%; }

                            @media (min-width: 992px) {
                                .col-md-2-4 { flex: 0 0 20%; max-width: 20%; }
                            }

                            @media (min-width: 768px) and (max-width: 991px) {
                                .col-md-2-4 { flex: 0 0 33.3333%; max-width: 33.3333%; }
                            }

                            @media (max-width: 767px) {
                                .col-md-2-4 { flex: 0 0 50%; max-width: 50%; }
                            }

                            .badge-success-subtle {
                            background-color: hsl(117.53deg 70.87% 79.8%);
                            color: #146c43;
                            font-size: 14px;
                            font-weight: 500;
                            padding: 7px 12px;
                            border-radius: 15px;
                            }

                            .redeem-category-navbar {
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                flex-wrap: wrap;
                                gap: 4px;
                            }

                            .redeem-category-wrapper {
                                position: relative;
                            }

                            .redeem-category-item {
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                padding: 9px 16px;
                                color: #132530;
                                font-size: 14px;
                                font-weight: 500;
                                border-radius: 6px 6px 0 0;
                                cursor: pointer;
                                white-space: nowrap;
                                transition: all 0.25s ease;
                                text-transform: uppercase;
                            }

                            .redeem-category-item:hover {
                                background-color: rgba(204, 0, 11, 0.08);
                                color: #cc000b;
                            }

                            .redeem-category-item.active {
                                background-color: #cc000b;
                                color: #fff;
                            }

                            .redeem-category-item i {
                                font-size: 10px;
                                transition: transform 0.25s ease;
                            }

                            .redeem-category-wrapper:hover .redeem-category-item i {
                                transform: rotate(180deg);
                            }

                            .redeem-category-dropdown {
                                position: absolute;
                                top: 100%;
                                left: 0;
                                min-width: 190px;
                                background: #fff;
                                border: 1px solid #e5e5e5;
                                border-radius: 0 0 6px 6px;
                                box-shadow: 0 5px 18px rgba(0, 0, 0, 0.12);
                                z-index: 9999;
                                padding: 5px 0;
                                display: none;
                            }

                            .redeem-category-wrapper:hover .redeem-category-dropdown {
                                display: block;
                            }

                            .redeem-category-child {
                                padding: 9px 15px;
                                font-size: 13px;
                                font-weight: 500;
                                color: #132530;
                                cursor: pointer;
                                white-space: nowrap;
                                transition: all 0.2s ease;
                            }

                            .redeem-category-child:hover,
                            .redeem-category-child.active {
                                background-color: #cc000b;
                                color: #fff;
                            }

                            .points-summary {
                                min-height: 70px;
                                background-color: #fff;
                                padding-right: 20px;
                                padding-left: 20px;
                                border-radius: 15px
                            }

                            .points-summary p {
                                font-size: 16px;
                            }

                            .offer-card {
                                background-color: #fff;
                                overflow: hidden;
                                transition: all 0.3s ease;
                            }

                            .offer-card:hover {
                                transform: translateY(-3px);
                            }

                            .offer-image {
                                height: 200px;
                                background-color: #fff;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                padding: 10px;
                                margin-top: 10px;
                            }

                            .offer-image img {
                                max-height: 100%;
                                max-width: 100%;
                                width: auto;
                                height: auto;
                                object-fit: contain;
                            }

                            .offer-desc {
                                font-size: 12px;
                                color: #777;
                                display: -webkit-box;
                                -webkit-line-clamp: 2;
                                -webkit-box-orient: vertical;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                word-break: break-word;
                            }

                            .bg-gift-cards {
                                background: #09a9c3 !important;
                                color: #fff !important;
                            }

                            .bg-home-essentials {
                                background: #ff7143 !important;
                                color: #fff !important;
                            }

                            .bg-travel {
                                background: #8c6e63 !important;
                                color: #fff !important;
                            }

                            .bg-fashion {
                                background: #9375cd !important;
                                color: #fff !important;
                            }

                            .bg-gaming {
                                background: #9ccd64 !important;
                                color: #fff !important;
                            }

                            .bg-tech {
                                background: #607d8b !important;
                                color: #fff !important;
                            }

                            .redeem-switch {
                                position: relative;
                                width: 62px;
                                height: 30px;
                                flex-shrink: 0;
                            }

                            .redeem-switch input {
                                display: none;
                            }

                            .redeem-switch label {
                                position: relative;
                                display: block;
                                width: 62px;
                                height: 30px;
                                margin: 0;
                                cursor: pointer;
                                background-color: #adb5bd;
                                border-radius: 30px;
                                transition: all 0.3s ease;
                            }

                            .redeem-switch label::before {
                                content: "";
                                position: absolute;
                                width: 24px;
                                height: 24px;
                                left: 3px;
                                top: 3px;
                                background-color: #fff;
                                border-radius: 50%;
                                transition: all 0.3s ease;
                                box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
                                z-index: 2;
                            }

                            .redeem-switch .switch-off,
                            .redeem-switch .switch-on {
                                position: absolute;
                                top: 50%;
                                transform: translateY(-50%);
                                color: #fff;
                                font-size: 13px;
                                font-weight: 700;
                                line-height: 1;
                            }

                            .redeem-switch .switch-off {
                                right: 15px;
                            }

                            .redeem-switch .switch-on {
                                left: 6px;
                                display: none;
                            }

                            .redeem-switch input:checked + label {
                                background-color: #cc000b;
                            }

                            .redeem-switch input:checked + label::before {
                                transform: translateX(32px);
                            }

                            .redeem-switch input:checked + label .switch-off {
                                display: none;
                            }

                            .redeem-switch input:checked + label .switch-on {
                                display: block;
                            }

                            @media (max-width: 767px) {
                                .redeem-category-navbar {
                                    justify-content: flex-start;
                                    overflow-x: auto;
                                    flex-wrap: nowrap;
                                    padding-bottom: 4px;
                                }

                                .redeem-category-item {
                                    padding: 8px 12px;
                                    font-size: 13px;
                                }

                                .redeem-category-dropdown {
                                    min-width: 180px;
                                }

                                .offer-image {
                                    height: 160px;
                                }

                                .points-summary {
                                    flex-direction: column;
                                    align-items: stretch !important;
                                    padding: 12px 15px;
                                }

                                .points-summary > div {
                                    justify-content: space-between !important;
                                    margin-left: 0 !important;
                                    margin-bottom: 8px;
                                }

                                .points-summary > div:last-child {
                                    margin-bottom: 0;
                                }
                            }
                        `}
                    </style>
                </main>
            )}
        </>
    );
};

export default RedeemPoints;